/**
 * The category question composer's two reads, through SoundGuys' proxies
 * (`pages/api/owner-notes/discussions/{reach,answered}.ts`). Signed-out readers read them too: no
 * bearer, no device. Each answers null on ANY failure (no route yet, 4xx/5xx, a network error, a
 * malformed body) and never throws — null is the composer's degrade path: no reach line, no slot,
 * and the question still posts.
 */
import { discussionsApi, isSignInError } from 'lib/community/discussions-api'
import type {
  DiscussionAnsweredResponse,
  DiscussionCard,
  DiscussionCreateResponse,
  DiscussionReachResponse,
} from 'lib/community/discussions-types'
import { OwnerApiError, ownerRoute } from './api'
import type { OwnerCredential } from './auth-bridge'
import {
  answeredOnly,
  countOrNull,
  questionBlocker,
  questionCreateBody,
  QUESTION_COPY,
  SLOT_LIMIT,
  type QuestionDraft,
} from './category-question'

async function readJson<T>(path: string, signal?: AbortSignal): Promise<T | null> {
  try {
    const res = await fetch(ownerRoute(path), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal,
    })
    if (res.status !== 200) {
      return null
    }
    return (await res.json()) as T
  } catch {
    return null
  }
}

/** Owners a question here would reach (with `product`: that product's owners). */
export async function fetchQuestionReach(
  categoryNodeId: string,
  productNodeId: string | null,
  signal?: AbortSignal
): Promise<number | null> {
  const qs = new URLSearchParams({ category: categoryNodeId })
  if (productNodeId) {
    qs.set('product', productNodeId)
  }
  const body = await readJson<DiscussionReachResponse>(
    `/discussions/reach?${qs.toString()}`,
    signal
  )
  return countOrNull(body?.owners)
}

/** Answered questions in the category (matching `q` when sent). null = no answer to trust. */
export async function fetchAnsweredQuestions(
  categoryNodeId: string,
  q: string | null,
  signal?: AbortSignal
): Promise<DiscussionCard[] | null> {
  const qs = new URLSearchParams({ category: categoryNodeId, limit: String(SLOT_LIMIT) })
  if (q) {
    qs.set('q', q)
  }
  const body = await readJson<DiscussionAnsweredResponse>(
    `/discussions/answered?${qs.toString()}`,
    signal
  )
  return Array.isArray(body?.items) ? answeredOnly(body.items) : null
}

export type QuestionSubmit =
  | { kind: 'blocked'; message: string }
  /** No usable session (none, or the one the proxy refused): open the sign-in dialog and post
   *  once a NEW session arrives. `rejectToken` = the token the proxy just refused. */
  | { kind: 'sign_in'; rejectToken: string | null }
  | { kind: 'posted'; answer: DiscussionCreateResponse }
  | { kind: 'error'; message: string; code: string }

/**
 * One press of Post, the discussions writer's rules: the title first; then a session the proxy
 * has not refused, else the sign-in (the draft is the caller's to keep); then the create through
 * `discussionsApi` with the question body. DealHunt's reader-safe `message` is shown verbatim.
 */
export async function submitQuestion(opts: {
  draft: QuestionDraft
  categoryNodeId: string
  credential: OwnerCredential | null
  rejectedToken: string | null
  create?: typeof discussionsApi.create
}): Promise<QuestionSubmit> {
  const blocker = questionBlocker(opts.draft)
  if (blocker) {
    return { kind: 'blocked', message: blocker }
  }
  const cred = opts.credential
  if (!cred || cred.token === opts.rejectedToken) {
    return { kind: 'sign_in', rejectToken: null }
  }
  const create = opts.create ?? discussionsApi.create
  try {
    const answer = await create(cred, questionCreateBody(opts.draft, opts.categoryNodeId))
    return { kind: 'posted', answer }
  } catch (e) {
    if (isSignInError(e)) {
      return { kind: 'sign_in', rejectToken: cred.token }
    }
    return e instanceof OwnerApiError
      ? { kind: 'error', message: e.message, code: e.code }
      : { kind: 'error', message: QUESTION_COPY.postFailed, code: 'error' }
  }
}
